/* eslint-disable @typescript-eslint/no-unused-vars */
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Pencil, ChevronDown, ChevronUp } from "lucide-react";
import { useWorkspaceObjectSchema } from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import usePreferencesStore from "../../../../store/preference/preferencesStore";
import { getAccountPinnedTraits, updateAccountPinnedTraits } from "../../../../store/preference/preferencesHelpers";
import MultiSelectDropdown from "../../../../shared/ui/dropdown/MultiSelectDropdown";
import KeyRoles from "./KeyRoles";
import LeftAccountBox from "./LeftAccountBox";
import IntegratedSources from "./IntegratedSources";

type Props = {
  account: any;
  contacts: any;
};

// QuickStat row matching the kindly-connect-share design
const QuickStat = ({
  label,
  value,
  valueClassName = "",
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) => (
  <div className="flex items-center justify-between">
    <span className="text-[11px] text-gray-500">{label}</span>
    <span className={`text-[11px] text-gray-900 ${valueClassName}`}>{value}</span>
  </div>
);

// Format a field value based on its data type
const formatFieldValue = (value: any, dataType?: string, displayFormat?: string): { text: string; className?: string } => {
  if (value === null || value === undefined || value === "") return { text: "—", className: "text-gray-400" };

  // Display format overrides
  if (displayFormat === "percentage" || dataType === "percentage") {
    const num = typeof value === "number" ? value : parseFloat(String(value));
    if (!Number.isNaN(num)) {
      const color = num >= 70 ? "text-green-600" : num >= 40 ? "text-yellow-600" : "text-red-600";
      return { text: `${num}%`, className: color };
    }
  }

  if (displayFormat === "currency") {
    const num = typeof value === "number" ? value : parseFloat(String(value));
    if (!Number.isNaN(num)) {
      if (Math.abs(num) >= 1_000_000) return { text: `$${(num / 1_000_000).toFixed(1)}M`, className: "font-mono tabular-nums" };
      if (Math.abs(num) >= 1_000) return { text: `$${num.toLocaleString("en-US")}`, className: "font-mono tabular-nums" };
      return { text: `$${num.toFixed(2)}`, className: "font-mono tabular-nums" };
    }
  }

  if (dataType === "datetime" && value) {
    try {
      const d = new Date(value);
      const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
      if (hasTime) {
        return { text: format(d, "MMM d, yyyy, h:mm a") };
      }
      return { text: format(d, "MMM d, yyyy") };
    } catch {
      return { text: String(value) };
    }
  }

  if (dataType === "boolean") {
    return { text: value ? "Yes" : "No" };
  }

  if (typeof value === "number") {
    return { text: value.toLocaleString("en-US"), className: "font-mono tabular-nums" };
  }

  return { text: String(value) };
};

const AccountProfileHeader = ({ account, contacts = [] }: Props) => {
  const [showAllTraits, setShowAllTraits] = useState(false);
  const [showTraitDropdownOpen, setShowTraitDropdownOpen] = useState(false);

  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const preferences = usePreferencesStore((state) => state.preferences);

  const [displayedTraits, setDisplayedTraits] = useState<string[]>([]);

  useEffect(() => {
    const pinnedTraits = getAccountPinnedTraits();
    if (pinnedTraits && pinnedTraits.length > 0) {
      setDisplayedTraits(pinnedTraits);
    }
  }, [preferences]);

  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({
    workspaceId,
    enabled: !!workspaceId,
  });
  const accountTraits = workspaceSchema?.fieldsByObjectSlug?.accounts || { list: [], obj: {} };

  const hasConnectedSources = Boolean(
    account?.hubspot_record_id || account?.salesforce_record_id
  );

  const onSelectTrait = async (trait: any) => {
    const newTraits = displayedTraits.includes(trait.name)
      ? displayedTraits.filter((name: any) => name !== trait.name)
      : [...displayedTraits, trait.name];

    const previousTraits = [...displayedTraits];
    setDisplayedTraits(newTraits);

    try {
      await updateAccountPinnedTraits(newTraits);
    } catch (error) {
      console.error("Failed to update account traits:", error);
      setDisplayedTraits(previousTraits);
    }
  };

  // Build pinned trait rows from preferences
  const pinnedTraitRows = useMemo(() => {
    if (!account || !displayedTraits?.length) return [];
    return displayedTraits
      .map((traitName: string) => {
        const traitDef = accountTraits?.obj?.[traitName];
        if (!traitDef || traitDef.exclude_display) return null;

        const fieldKey = traitName.replace("accounts.", "");
        const value = account[fieldKey];
        const formatted = formatFieldValue(value, traitDef.data_type, traitDef.display_format);
        return {
          key: traitName,
          display_name: traitDef.display_name || fieldKey.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
          formatted,
        };
      })
      .filter(Boolean);
  }, [account, displayedTraits, accountTraits?.obj]);

  const visibleTraits = showAllTraits ? pinnedTraitRows : pinnedTraitRows.slice(0, 5);
  const hasMoreTraits = pinnedTraitRows.length > 5;

  return (
    <div className="space-y-5">
      {/* Avatar & Name */}
      <LeftAccountBox account={account} />

      {/* Quick Stats */}
      <div className="space-y-2">
        <QuickStat label="Joined On" value={account?.record_created_at ? format(new Date(account.record_created_at), "MMM d, yyyy") : "—"} />
        <QuickStat label="Total Users" value={account?.total_user_count?.toLocaleString() || "—"} valueClassName="font-mono tabular-nums" />
        <QuickStat label="Last Seen" value={account?.last_seen ? format(new Date(account.last_seen), "MMM d, yyyy") : "—"} />
      </div>

      {/* Integrated Sources */}
      {hasConnectedSources && (
        <IntegratedSources account={account} />
      )}

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* ACCOUNT TRAITS */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Account Traits</span>
          <MultiSelectDropdown
            selected={displayedTraits}
            position="bottom-start"
            setSelected={onSelectTrait}
            isOpen={showTraitDropdownOpen}
            setIsOpen={setShowTraitDropdownOpen}
            list={accountTraits?.list?.filter((trait: any) => !trait.exclude_display) || []}
          >
            <Pencil className="cursor-pointer w-3.5 h-3.5 text-gray-400 hover:text-gray-700" />
          </MultiSelectDropdown>
        </div>
        <div className="space-y-2">
          {visibleTraits.length > 0 ? (
            visibleTraits.map((trait: any) => (
              <QuickStat
                key={trait.key}
                label={trait.display_name}
                value={trait.formatted.text}
                valueClassName={trait.formatted.className || ""}
              />
            ))
          ) : (
            <p className="text-[11px] text-gray-400 text-center py-2">
              No pinned traits. Click the pencil to add.
            </p>
          )}
        </div>
        {hasMoreTraits && (
          <button
            type="button"
            onClick={() => setShowAllTraits(!showAllTraits)}
            className="w-full mt-2 h-7 text-xs text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
          >
            {showAllTraits ? (
              <>See less <ChevronUp className="h-3 w-3" /></>
            ) : (
              <>See more <ChevronDown className="h-3 w-3" /></>
            )}
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* KEY ROLES */}
      {account?.external_id && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Key Roles</span>
          </div>
          <KeyRoles
            key={account?.external_id}
            accountId={account?.external_id}
            contacts={contacts}
            owner={account?.account_champion_id}
            csm={account?.csm_id}
            ae={account?.ae_id}
            containerClass="w-full h-auto"
            collapsible={false}
            vertical
          />
        </div>
      )}
    </div>
  );
};

export default AccountProfileHeader;
